/**
 * Client for the config analytics API (config-analytics-api): consent for MCP
 * connections and the list of connections.
 *
 * MCP clients connect with standard OAuth. The API sends the browser to the
 * consent page here, a logged-in admin approves the connection for up to 90
 * days, and the browser goes back to the client, which gets its own token. The
 * portal never sees a token.
 */

import { getToken } from './api-utils'

import { config } from '@/configs/api.config'

export const CONNECTION_STATUS = {
  ACTIVE: 'ACTIVE',
  REVOKED: 'REVOKED',
  EXPIRED: 'EXPIRED'
} as const

export type ConnectionStatus =
  (typeof CONNECTION_STATUS)[keyof typeof CONNECTION_STATUS]

export interface Connection {
  id: string
  client_name: string
  duration_hours: number
  status: ConnectionStatus
  user_id: string
  email?: string
  created_at: string
  expires_at: string
  revoked_by?: string
  revoked_at?: string
}

export interface PendingAuthorization {
  id: string
  client_name: string
  redirect_host: string
  created_at: string
  expires_at: string
}

export class ConfigAnalyticsApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message)
  }
}

export const configAnalyticsUrl = () =>
  (config as Record<string, string>).CONFIG_ANALYTICS

const apiFetch = async <T>(path: string, init?: RequestInit): Promise<T> => {
  const token = await getToken()

  const res = await fetch(`${configAnalyticsUrl()}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...init?.headers
    }
  })

  if (!res.ok) {
    // epilot error shape: { status, error }
    const body = (await res.json().catch(() => ({}))) as {
      error?: string
      message?: string
    }

    throw new ConfigAnalyticsApiError(
      res.status,
      body.message ?? body.error ?? `Config analytics API error ${res.status}`
    )
  }

  return res.json() as Promise<T>
}

const id = (value: string) => encodeURIComponent(value)

export const configAnalyticsClient = {
  getAuthorization: (requestId: string) =>
    apiFetch<PendingAuthorization>(`/v1/authorizations/${id(requestId)}`),

  /** Returns where to send the browser: the MCP client's callback with a code. */
  approve: (requestId: string, input: { duration_hours: number }) =>
    apiFetch<{ redirect_to: string }>(
      `/v1/authorizations/${id(requestId)}:approve`,
      { method: 'POST', body: JSON.stringify(input) }
    ),

  deny: (requestId: string) =>
    apiFetch<{ redirect_to: string }>(
      `/v1/authorizations/${id(requestId)}:deny`,
      { method: 'POST', body: '{}' }
    ),

  /** `all` is for admins; the API answers 403 for everyone else. */
  listConnections: async (scope: 'mine' | 'all') =>
    (
      await apiFetch<{ results: Connection[] }>(
        `/v1/connections?scope=${scope}`
      )
    ).results,

  revoke: (connectionId: string) =>
    apiFetch<Connection>(`/v1/connections/${id(connectionId)}:revoke`, {
      method: 'POST',
      body: '{}'
    })
}
